import { gql, useQuery } from '@apollo/client';
import { Loading } from '@arcadiapower/shrike';
import { ValidatingInput, InputComponentProps } from '~/components/validating-input';
import {
  Flex_FetchUtilityAccountNumberRulesQuery,
  Flex_FetchUtilityAccountNumberRulesQueryVariables,
} from '~/typings/graphql';
import { UTILITY_IDS } from '~/utils/constants';

export const FETCH_ACCOUNT_NUMBER_RULES = gql`
  query Flex_FetchUtilityAccountNumberRules($utilityId: Int!) {
    utility(id: $utilityId) {
      rules: accountNumberRules {
        example
        pattern
      }
    }
  }
`;

interface UtilityAccountNumberProps extends InputComponentProps {
  hideRequirements?: boolean;
  utilityId?: number;
  utilityName?: string;
  label?: string; // this is getting used as an override in the spread, TODO: make it more explicit
}

export const UtilityAccountNumber = ({
  hideRequirements,
  utilityId,
  utilityName,
  ...props
}: UtilityAccountNumberProps) => {
  const { data, loading } = useQuery<
    Flex_FetchUtilityAccountNumberRulesQuery,
    Flex_FetchUtilityAccountNumberRulesQueryVariables
  >(FETCH_ACCOUNT_NUMBER_RULES, {
    fetchPolicy: 'no-cache',
    skip: !utilityId,
    // @ts-expect-error — undefined input handled by skip param
    variables: { utilityId },
  });

  if (loading) return <Loading />;

  const rules = data?.utility?.rules || [];
  const examples = rules.map(rule => rule?.example);
  const patterns = rules.map(rule => rule?.pattern);
  const requirements = examples.length ? `Example: ${examples[examples.length - 1]}` : '';
  const label = utilityName ? `Account number for ${utilityName}` : 'Account number';
  const isDominionVA = utilityId === UTILITY_IDS['Dominion Energy VA'];
  const dominionVARegex = /^[0-9]{8,12}$/;

  const validate = ({ value }) => {
    if (!value) return false;
    if (!rules.length) return true;
    let isValid = !!patterns.find(pattern => new RegExp(pattern!).test(value));
    isValid = isDominionVA ? dominionVARegex.test(value) : isValid;
    if (!isValid) {
      /**
       * TODO: this is for showing red highlight around the
       * input box. Remove this when proper error reporting
       * is done.
       */
      return <span>Please enter a valid account number.</span>;
    }
    return isValid;
  };

  return (
    <ValidatingInput
      debounceValidation={0}
      disabled={loading}
      label={label}
      name="utilityAccountNumber"
      requirements={hideRequirements ? '' : requirements}
      validate={validate}
      {...props}
    />
  );
};
